import { Controller } from '@hotwired/stimulus';

import {
  interpolate,
  isoParts,
  isoToDisplay,
  periodDateErrorKey,
  periodSummary,
  shiftDays,
  shiftMonths,
} from '../utils/planning_calendar_date_formats.js';
import { isInteractiveTarget } from '../utils/interactive_target.js';
import { anchorEndOnStart } from '../utils/planning_period_end_anchor.js';
import { periodErrors } from '../utils/planning_period_errors.js';

// Une période touche-t-elle l'année ? Dates absentes ou année inconnue : oui (rien n'est masqué à tort).
export function periodTouchesYear(start, end, year) {
  const from = isoParts(start);
  const to = isoParts(end);
  if (!from || !to || !year) return true;
  return from[0] <= year && to[0] >= year;
}

// Saisie du libellé : enregistrement après ce délai sans frappe.
const LABEL_DEBOUNCE_MS = 600;
// Après un 200, la réponse remplace l'empreinte du calendrier (`#agency_calendar_fingerprint`) APRÈS
// `turbo:submit-end` (Turbo rend les flux après un repaint). Un renvoi fait avant porterait l'ancienne
// empreinte et recevrait un 409 : on attend le flux de l'empreinte, au plus ce délai.
const FINGERPRINT_WAIT_MS = 2000;
// L'annonceur se vide puis se remplit, pour qu'un lecteur d'écran relise un texte identique.
const ANNOUNCE_DELAY_MS = 100;

const FINGERPRINT_ID = 'agency_calendar_fingerprint';
// Le message d'un refus sans ligne à désigner (409 : texte « stale »), rendu par le serveur.
const ALERT_ID = 'agency_calendar_alert';
// Le frame de l'année (légende, ‹ ›, aide, grille) : un clic hors de lui abandonne la plage commencée.
const YEAR_FRAME_ID = 'agency_calendar_year';
const ID_PLACEHOLDER = '__ID__';
// Champs postés qui ne sont pas des données du calendrier : exclus de la comparaison d'états.
const VOLATILE_FIELDS = new Set(['calendar_fingerprint', 'authenticity_token', 'year', '_method']);

const DAY_SELECTOR = '[data-agency-calendar-target~="day"]';
const DATE_PICKER_ROOT = '[data-controller~="date-picker"]';
const DATE_PICKER_DISPLAY = '[data-date-picker-target~="display"]';
// Période NOUVELLE (brouillon, AMENDEMENT 1) : ses champs ne partent qu'avec « Ajouter la période ».
const UNSAVED_ATTRIBUTE = 'data-agency-calendar-unsaved';
const DRAFT_ACTIONS_SELECTOR = '[data-agency-calendar-draft-actions]';
const COMMIT_SELECTOR = '[data-action~="agency-calendar#commitDraft"]';
const CANCEL_DRAFT_SELECTOR = '[data-action~="agency-calendar#cancelDraft"]';
const HINT_ATTRIBUTE = 'data-agency-calendar-hint';
const STASHED_NAME_ATTRIBUTE = 'data-agency-calendar-name';
// Gabarit brut du nom du crayon (« Modifier « %{label} » »), rendu par le serveur sur le crayon lui-même.
const EDIT_LABEL_ATTRIBUTE = 'data-agency-calendar-edit-label';
const PERIOD_LABEL_SELECTOR = '[data-period-label]';
const DRAFT_CLASS = 'is-draft';
const HIGHLIGHT_CLASS = 'is-highlighted';
const PERIOD_HOVER_CLASS = 'is-period-hover';

// Le résumé d'une période (A·e) vit dans `utils/planning_calendar_date_formats.js`, partagé avec le tiroir
// « Réglages » du planning ; ré-exporté ici pour la parité Ruby
// (`test/integration/organization_settings/planning_calendar_summary_js_parity_test.rb`).
export { periodSummary };

/**
 * agency-calendar — calendrier annuel de l'agence (paramètres, onglet « Planning », planches A·a–g).
 *
 * Contrat serveur ↔ JS : plan `docs/superpowers/plans/2026-10-29-jours-non-travailles.md`, T4. Le serveur rend
 * les couleurs (classes figées des cellules), les textes et la ligne modèle ; ce contrôleur pose seulement deux
 * classes réservées, `is-draft` (plage en cours de tracé) et `is-highlighted` (férié mis en avant), dont les
 * couleurs vivent dans le CSS. Aucun texte en dur (tout vient de `messages`), aucun HTML fabriqué : les textes
 * passent par `textContent`, la ligne nouvelle est un clone du `<template>` rendu par le serveur.
 *
 * Création d'une période (A.11–A.13, A·e, A·g ; AMENDEMENT 1 du contrat) : premier `pick` sur un jour (hors
 * férié) = début, l'aide devient `pick_last` ; `preview` (survol, flèches) colore la plage ; second `pick` = fin,
 * bornes ordonnées, clone du modèle sous un id `crypto.randomUUID()`, Du / Au posés (champ ISO caché ET
 * affichage JJ/MM/AAAA), éditeur ouvert, focus sur Libellé. « Ajouter » ouvre la même ligne sans dates (aide
 * `pick_first`). Cette ligne est un BROUILLON (`data-agency-calendar-unsaved`) : jamais postée par les
 * enregistrements automatiques, ses jours restent `is-draft`, et UN seul brouillon existe (un nouveau double
 * clic le remplace). « Ajouter la période » (`commitDraft`) est désactivé tant qu'une date manque (décrit par
 * le nœud de son `aria-describedby` rendu, rempli de `invalid_dates` s'il est vide) ou qu'une erreur est
 * affichée (décrit par `#period_<id>_errors`) ; un changement de Du / Au efface l'erreur et le réactive. Il
 * poste le brouillon avec le reste : 200 → ligne normale (bloc `[data-agency-calendar-draft-actions]` et
 * marqueur retirés, éditeur fermé) ; 422 → brouillon en erreur. « Annuler » (`cancelDraft`) retire la ligne
 * sans rien envoyer. Une période EXISTANTE garde l'enregistrement automatique. Le premier clic survit à la
 * navigation d'année (ce contrôleur est l'ancêtre du frame) : `turbo:frame-load` repeint la plage. Échap, ou un
 * clic hors de ce frame (écoute `click` sur `document`), abandonne la plage commencée (retour du 30/10).
 *
 * Férié (A.7) : `pick` sur une cellule `data-holiday-key` hors tracé ouvre le `<details>` des fériés et met en
 * avant la ligne `data-holiday-row` et la cellule ; aucune plage n'est commencée. Pendant un tracé (après
 * « Ajouter », aide `pick_first`, ou après le premier jour), c'est un jour comme un autre (QA-JNT-58).
 *
 * Crayon : son nom (`aria-label`, `title`) suit le libellé AFFICHÉ de la ligne (`[data-period-label]`), par le
 * gabarit brut `data-agency-calendar-edit-label` qu'il porte : après chaque résumé peint ici ou rendu par un flux
 * (le crayon vit hors du résumé, aucune réponse ne le remplace).
 *
 * Ligne de période au clavier (QA-JNT-72) : Échap dans une ligne ouverte (enregistrée) la referme et rend le
 * focus à son crayon (un Échap déjà consommé, par le calendrier du sélecteur de date, est ignoré) ; Entrée dans
 * Libellé d'un brouillon = « Ajouter la période » si les dates le permettent. Pendant l'envoi, un focus posé sur
 * « Ajouter la période » (qui se désactive) passe à la ligne (`tabindex="-1"`) ; au 200, l'éditeur se ferme et
 * un focus resté dans la ligne passe à son crayon : jamais sur `<body>`.
 *
 * Enregistrement automatique (A.10, RF-R3) : `change` d'un champ nommé du formulaire (interrupteurs, dates),
 * suppression d'une ligne → `save()` ; frappe dans un libellé → `save()` après 600 ms. UNE requête en vol à la
 * fois, par `form.requestSubmit()` (Turbo et CSRF, jamais de `fetch`) : un changement pendant le vol lève
 * `dirty`, et l'état COURANT part quand la requête se termine (après le remplacement de l'empreinte si elle a
 * réussi). Un état identique au dernier enregistré ne repart pas. Succès : « Enregistré » + annonce ; échec :
 * statut caché, et chaque ligne a `aria-invalid` sur Du / Au tant que son nœud `#period_<id>_errors` n'est pas
 * vide (resynchronisé après chaque flux Turbo rendu).
 *
 * Courses de la rafale (QA-JNT-69) : « Ajouter la période » pendant un vol AJOUTE le brouillon à ceux qui
 * attendent (`committingRows`) ; aucun brouillon confirmé n'est plus repris par un double clic. Au 200, les
 * drapeaux (`awaitingFingerprint`) sont posés AVANT que le focus passe au crayon : le `change` que le navigateur
 * émet alors sur le Libellé quitté attend la nouvelle empreinte. 409 : `conflicted`, plus rien ne part (le
 * message du serveur reste, « Enregistré » ne revient pas, les brouillons restent à l'écran, « Ajouter la
 * période » désactivé et décrit par `#agency_calendar_alert`).
 *
 * Clavier (`key`, grille à un seul arrêt de tabulation) : flèches ± 1 jour / ± 7 jours, PageUp / PageDown
 * ± 1 mois, Entrée ou Espace = `pick`, Échap = abandon de la plage en cours.
 *
 * Targets :
 *   • form           — LE formulaire posté (`requestSubmit`)
 *   • day            — chaque cellule de jour (`data-date`, `data-day-label`, `data-holiday-key` facultatif)
 *   • help           — l'aide sous la légende
 *   • periodList     — la liste des lignes `id="period_<id>"`
 *   • periodTemplate — `<template>` de la ligne, `__ID__` à la place de l'id
 *   • empty          — l'état vide (A.14)
 *   • status         — deux nœuds `[data-state="saving"]` / `[data-state="saved"]`
 *   • announcer      — région `role="status"` toujours présente
 *
 * Résumé d'un brouillon (A·e) : `[data-period-label]`, `[data-period-dates]`, `[data-period-days]` de la ligne
 * suivent Libellé, Du et Au (`periodSummary`, par `textContent`) ; après le 200, le serveur rend le sien. Des
 * dates refusées par les règles du concern (années, 366 jours, fin avant début) n'ont pas de « N j » : le
 * message d'erreur suffit (QA-JNT-59).
 *
 * Values :
 *   • messages (Object) — `help`, `pick_first`, `pick_last` (`%{date}`), `saved`, `saving`, `invalid_dates`,
 *                         et pour le résumé : `default_label`, `new_label` (brouillon sans libellé), `days_short`,
 *                         `period_formats`, `short_weekdays`,
 *                         `short_months` (`PlanningCalendarView#messages`)
 *   • calendar (Object) — calendrier des « N j » (`PlanningCalendarView#lost_days_calendar`)
 *   • year     (Number) — année affichée au rendu de la page
 *
 * Actions : `pick`, `preview`, `leave`, `key` (cellules ; hors tracé, `preview` marque la période survolée
 *   (`is-period-hover`) et `pick` sur un jour de période ouvre son édition, retour du 30/10), `add` (« Ajouter »), `edit` (crayon, `aria-controls`),
 *   `remove` (« Supprimer »), `commitDraft` (« Ajouter la période »), `cancelDraft` (« Annuler » du
 *   brouillon), `cancel` (abandon de la plage en cours, comme Échap).
 *
 * @example
 * <div data-controller="agency-calendar" data-agency-calendar-year-value="2027"
 *      data-agency-calendar-messages-value='{"help":"…","pick_last":"Début : %{date}. …"}'>
 *   <turbo-frame id="agency_calendar_year">
 *     <td role="gridcell" tabindex="0" data-agency-calendar-target="day" data-date="2027-04-19"
 *         data-day-label="lundi 19 avril"
 *         data-action="click->agency-calendar#pick mouseenter->agency-calendar#preview keydown->agency-calendar#key">19</td>
 *   </turbo-frame>
 *   <form data-agency-calendar-target="form">…<ul data-agency-calendar-target="periodList">…</ul></form>
 * </div>
 */
export default class extends Controller {
  static targets = [
    'form',
    'day',
    'help',
    'periodList',
    'periodTemplate',
    'empty',
    'status',
    'announcer',
  ];

  static values = { messages: Object, calendar: Object, year: Number };

  connect() {
    this.connected = true;
    this.draftStart = null;
    this.draftHover = null;
    this.draftStartLabel = '';
    this.helpKey = null;
    this.highlightedKey = null;
    this.focusDate = null;
    this.inFlight = false;
    this.dirty = false;
    this.sentState = null;
    this.submitted = false;
    this.writing = false;
    this.awaitingFingerprint = false;
    this.fingerprintRefreshed = false;
    this.conflicted = false;
    // Brouillons confirmés par « Ajouter la période » et pas encore enregistrés (QA-JNT-69) : plusieurs à la fois
    // quand on confirme pendant un vol. Aucun n'est plus « le brouillon » modifiable que réutilise un double clic.
    this.committingRows = new Set();
    this.sentCommitRows = new Set();
    this.defaultHelp = this.hasHelpTarget ? this.helpTarget.textContent : '';
    this.lastSavedState = this.hasFormTarget
      ? this.#withoutUnsavedRows(() => this.#formState())
      : null;

    this.onChange = this.handleChange.bind(this);
    this.onInput = this.handleInput.bind(this);
    this.onSubmit = this.handleSubmit.bind(this);
    this.onSubmitEnd = this.handleSubmitEnd.bind(this);
    this.onFrameLoad = this.handleFrameLoad.bind(this);
    this.onBeforeStreamRender = this.handleBeforeStreamRender.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.element.addEventListener('change', this.onChange);
    this.element.addEventListener('input', this.onInput);
    this.element.addEventListener('keydown', this.onKeydown);
    this.element.addEventListener('submit', this.onSubmit);
    this.element.addEventListener('turbo:submit-end', this.onSubmitEnd);
    this.element.addEventListener('turbo:frame-load', this.onFrameLoad);
    // Les flux Turbo sont insérés hors de cet élément : on les écoute sur `document`.
    document.addEventListener('turbo:before-stream-render', this.onBeforeStreamRender);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnect() {
    this.connected = false;
    this.element.removeEventListener('change', this.onChange);
    this.element.removeEventListener('input', this.onInput);
    this.element.removeEventListener('keydown', this.onKeydown);
    this.element.removeEventListener('submit', this.onSubmit);
    this.element.removeEventListener('turbo:submit-end', this.onSubmitEnd);
    this.element.removeEventListener('turbo:frame-load', this.onFrameLoad);
    document.removeEventListener('turbo:before-stream-render', this.onBeforeStreamRender);
    document.removeEventListener('click', this.onDocumentClick);
    clearTimeout(this.labelTimer);
    clearTimeout(this.fingerprintTimer);
    clearTimeout(this.announceTimer);
  }

  // ── Calendrier : deux clics ───────────────────────────────────────────────────────────────────────────────

  pick(event) {
    const cell = this.#cellFrom(event);
    if (cell) this.#pickCell(cell);
  }

  // Hors tracé, le survol d'un jour de période enregistrée marque toute la période (retour de Nicolas, 30/10) ;
  // pendant un tracé, seule la plage se colore.
  preview(event) {
    const cell = this.#cellFrom(event);
    if (!cell) return;
    if (!this.draftStart) {
      this.#paintPeriodHover(this.#periodRowAt(cell.dataset.date));
      return;
    }
    this.draftHover = cell.dataset.date;
    this.#paintDraft();
  }

  leave() {
    this.#paintPeriodHover(null);
  }

  cancel() {
    this.#clearDraft();
    this.#setHelp(null);
  }

  // Un clic hors du calendrier abandonne la plage commencée, comme Échap (retour de Nicolas, 30/10). « Dans » =
  // le frame de l'année : ‹ › y sont, une plage peut enjamber deux années (le premier clic survit au frame-load).
  handleDocumentClick(event) {
    if (!this.draftStart) return;
    const target = event?.target;
    if (typeof target?.closest === 'function' && target.closest(`#${YEAR_FRAME_ID}`)) return;
    this.cancel();
  }

  key(event) {
    const cell = this.#cellFrom(event);
    if (!cell) return;
    const date = cell.dataset.date;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#pickCell(cell);
      return;
    }
    if (event.key === 'Escape') {
      if (!this.draftStart) return;
      event.preventDefault();
      this.cancel();
      return;
    }

    const moves = {
      ArrowLeft: () => shiftDays(date, -1),
      ArrowRight: () => shiftDays(date, 1),
      ArrowUp: () => shiftDays(date, -7),
      ArrowDown: () => shiftDays(date, 7),
      PageUp: () => shiftMonths(date, -1),
      PageDown: () => shiftMonths(date, 1),
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();

    const next = this.#cellFor(move());
    if (!next) return;
    this.#rove(next, { focus: true });
    if (this.draftStart) {
      this.draftHover = next.dataset.date;
      this.#paintDraft();
    }
  }

  #pickCell(cell) {
    const date = cell.dataset.date;
    if (!isoParts(date)) return;
    this.#rove(cell);

    if (!this.draftStart) {
      // Hors tracé seulement (QA-JNT-58) : après « Ajouter », un férié peut être le premier jour.
      if (cell.dataset.holidayKey && this.helpKey !== 'pick_first') {
        this.#highlightHoliday(cell.dataset.holidayKey);
        return;
      }
      this.#highlightHoliday(null);
      // Un jour de période enregistrée ouvre son édition au lieu d'en commencer une autre (retour du 30/10) ; même
      // exception que le férié après « Ajouter » (une plage qui y commence chevauche, A.15 le dit).
      const periodRow = this.helpKey !== 'pick_first' && this.#periodRowAt(date);
      if (periodRow) {
        this.#paintPeriodHover(null);
        this.#closeOtherEditors(periodRow);
        this.#openEditor(periodRow, { focus: true });
        periodRow.scrollIntoView?.({ block: 'nearest' });
        return;
      }
      this.#paintPeriodHover(null);
      this.draftStart = date;
      this.draftHover = date;
      this.draftStartLabel = cell.dataset.dayLabel || '';
      this.#setHelp('pick_last');
      this.#paintDraft();
      return;
    }

    const [start, end] =
      this.draftStart <= date ? [this.draftStart, date] : [date, this.draftStart];
    this.#clearDraft();
    this.#setHelp(null);
    this.#createPeriod(start, end);
  }

  #clearDraft() {
    this.draftStart = null;
    this.draftHover = null;
    this.draftStartLabel = '';
    this.#paintDraft();
  }

  // `is-draft` : la plage en cours de tracé et les jours du brouillon (A·e, A·g).
  #paintDraft() {
    const ranges = [];
    if (this.draftStart) ranges.push([this.draftStart, this.draftHover || this.draftStart].sort());
    this.#rows()
      .filter((row) => row.hasAttribute(UNSAVED_ATTRIBUTE))
      .forEach((row) => {
        const bounds = ['start', 'end'].map((bound) => this.#dateField(row, bound)?.value);
        if (bounds.every((iso) => isoParts(iso))) ranges.push(bounds.sort());
      });
    this.dayTargets.forEach((cell) => {
      const date = cell.dataset.date;
      const inside = ranges.some(([from, to]) => date >= from && date <= to);
      cell.classList.toggle(DRAFT_CLASS, inside);
    });
  }

  #highlightHoliday(key) {
    this.highlightedKey = key;
    this.#paintHighlight();
    if (!key) return;

    const row = this.#holidayRow(key);
    if (!row) return;
    for (let details = row.closest('details'); details; ) {
      details.toggleAttribute('open', true);
      details = details.parentElement ? details.parentElement.closest('details') : null;
    }
    row.scrollIntoView?.({ block: 'nearest' });
  }

  #paintHighlight() {
    const key = this.highlightedKey;
    this.element.querySelectorAll('[data-holiday-row]').forEach((row) => {
      row.classList.toggle(HIGHLIGHT_CLASS, Boolean(key) && row.dataset.holidayRow === key);
    });
    this.dayTargets.forEach((cell) => {
      cell.classList.toggle(HIGHLIGHT_CLASS, Boolean(key) && cell.dataset.holidayKey === key);
    });
  }

  // Une seule période ouverte à la fois quand le calendrier ou « Ajouter » en ouvre une (retour de Nicolas, 30/10) :
  // les autres se referment, sauf un brouillon ou une ligne en erreur, dont la saisie et le message doivent rester
  // visibles. Puis le filtre de l'année : une ligne d'une autre année n'était montrée que parce qu'ouverte.
  #closeOtherEditors(keep) {
    this.#rows()
      .filter((row) => row !== keep && !row.hasAttribute(UNSAVED_ATTRIBUTE) && !this.#hasError(row))
      .forEach((row) => this.#closeEditor(row));
    this.#refreshEmpty();
  }

  // La période enregistrée qui contient `date` (ligne cachée d'une autre année comprise), ou null.
  #periodRowAt(date) {
    if (!isoParts(date)) return null;
    return (
      this.#rows().find((row) => {
        if (row.hasAttribute(UNSAVED_ATTRIBUTE)) return false;
        const [start, end] = ['start', 'end'].map((bound) => this.#dateField(row, bound)?.value);
        return isoParts(start) && isoParts(end) && start <= date && date <= end;
      }) ?? null
    );
  }

  // `is-period-hover` sur les jours de la période de `row` (aucun sans ligne) ; sa couleur vit dans
  // `application.tailwind.css` (contrat #18).
  #paintPeriodHover(row) {
    const [start, end] = row
      ? ['start', 'end'].map((bound) => this.#dateField(row, bound)?.value)
      : [null, null];
    this.dayTargets.forEach((cell) => {
      const date = cell.dataset.date;
      cell.classList.toggle(PERIOD_HOVER_CLASS, Boolean(row) && date >= start && date <= end);
    });
  }

  #holidayRow(key) {
    return [...this.element.querySelectorAll('[data-holiday-row]')].find(
      (row) => row.dataset.holidayRow === key
    );
  }

  // Arrêt de tabulation unique de la grille : il suit le jour cliqué ou atteint au clavier.
  #rove(cell, { focus = false } = {}) {
    this.focusDate = cell.dataset.date;
    this.dayTargets.forEach((other) => other.setAttribute('tabindex', other === cell ? '0' : '-1'));
    if (focus) cell.focus();
  }

  #cellFrom(event) {
    const node = event?.currentTarget;
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest(DAY_SELECTOR);
  }

  #cellFor(date) {
    if (!date) return null;
    return this.dayTargets.find((cell) => cell.dataset.date === date) ?? null;
  }

  // ── Aide ──────────────────────────────────────────────────────────────────────────────────────────────────

  #setHelp(key) {
    this.helpKey = key;
    this.#paintHelp();
  }

  #paintHelp() {
    if (!this.hasHelpTarget) return;
    const messages = this.messagesValue || {};
    let text = messages.help ?? this.defaultHelp;
    if (this.helpKey === 'pick_last' && messages.pick_last) {
      text = messages.pick_last.replaceAll('%{date}', this.draftStartLabel);
    } else if (this.helpKey && messages[this.helpKey]) {
      text = messages[this.helpKey];
    }
    this.helpTarget.textContent = text;
  }

  // ── Lignes de période ─────────────────────────────────────────────────────────────────────────────────────

  add() {
    this.#clearDraft();
    const pending = this.#draftRow();
    if (pending) {
      this.#openEditor(pending, { focus: true });
      this.#setHelp('pick_first');
      return;
    }

    const row = this.#cloneRow();
    if (!row) return;
    row.setAttribute(UNSAVED_ATTRIBUTE, '');
    this.periodListTarget.prepend(row);
    this.#closeOtherEditors(row);
    this.#refreshEmpty();
    this.#paintSummary(row);
    this.#syncDraftActions(row);
    this.#openEditor(row, { focus: true });
    this.#setHelp('pick_first');
  }

  // « Ajouter la période » : le brouillon part avec le reste du formulaire.
  commitDraft(event) {
    const row = this.#rowOf(event?.currentTarget);
    if (!row || !row.hasAttribute(UNSAVED_ATTRIBUTE) || this.committingRows.has(row)) return;
    if (this.conflicted || !this.#canCommit(row)) {
      this.#syncDraftActions(row);
      return;
    }
    // Le bouton se désactive pendant l'envoi : un focus posé dessus tomberait sur `<body>` (QA-JNT-72).
    if (document.activeElement === row.querySelector(COMMIT_SELECTOR)) this.#focusRow(row);
    this.committingRows.add(row);
    this.#syncDraftActions(row);
    this.save();
  }

  // ── Clavier dans une ligne de période (QA-JNT-72) ─────────────────────────────────────────────────────────

  handleKeydown(event) {
    if (event.defaultPrevented || event.isComposing) return;
    const row = this.#rowOf(event.target);
    if (!row) return;
    if (event.key === 'Escape') this.#escapeRow(event, row);
    else if (event.key === 'Enter') this.#enterInLabel(event, row);
  }

  // Échap : la ligne ouverte se referme, le focus revient à son crayon. Un brouillon n'a pas de crayon visible
  // (« Annuler » et « Ajouter la période » le remplacent) : Échap n'y fait rien, la saisie reste.
  #escapeRow(event, row) {
    const editor = this.#editorOf(row);
    if (!editor || editor.hidden || row.hasAttribute(UNSAVED_ATTRIBUTE)) return;
    event.preventDefault();
    this.#closeEditor(row);
    this.#editButtonOf(row)?.focus();
  }

  // Entrée dans Libellé d'un brouillon : « Ajouter la période » (le formulaire n'a pas de bouton d'envoi, Entrée
  // n'y fait rien d'autre). Dates incomplètes ou en erreur : rien ne part, le bouton reste décrit par sa raison.
  #enterInLabel(event, row) {
    if (!row.hasAttribute(UNSAVED_ATTRIBUTE) || event.target !== this.#labelField(row)) return;
    event.preventDefault();
    if (this.#canCommit(row)) this.commitDraft({ currentTarget: event.target });
  }

  #focusRow(row) {
    if (!row.hasAttribute('tabindex')) row.setAttribute('tabindex', '-1');
    row.focus();
  }

  // « Annuler » du brouillon : la ligne et sa plage disparaissent, rien n'est envoyé.
  cancelDraft(event) {
    const row = this.#rowOf(event?.currentTarget) ?? this.#draftRow();
    if (!row || !row.hasAttribute(UNSAVED_ATTRIBUTE) || this.committingRows.has(row)) return;
    row.remove();
    this.#clearDraft();
    this.#setHelp(null);
    this.#refreshEmpty();
    this.#focusAfterRemoval(null);
  }

  // Clic sur la ligne résumée (retour de Nicolas, 30/10) : comme le crayon, qui reste le bouton du clavier et des
  // lecteurs d'écran (un clic SUR lui garde sa propre action). Un brouillon n'a pas de crayon : rien.
  editFromRow(event) {
    if (isInteractiveTarget(event, event.currentTarget)) return;
    const row = this.#rowOf(event.currentTarget);
    if (!row || row.hasAttribute(UNSAVED_ATTRIBUTE)) return;
    const pencil = this.#editButtonOf(row);
    if (pencil) this.edit({ currentTarget: pencil });
  }

  edit(event) {
    const row = this.#rowOf(event?.currentTarget);
    if (!row) return;
    const editor = this.#editorOf(row);
    if (!editor) return;
    if (editor.hidden) this.#openEditor(row, { focus: true });
    else this.#closeEditor(row);
  }

  remove(event) {
    const row = this.#rowOf(event?.currentTarget);
    if (!row) return;
    const wasUnsaved = row.hasAttribute(UNSAVED_ATTRIBUTE);
    const rows = this.#rows();
    const index = rows.indexOf(row);
    const neighbour = rows[index + 1] ?? rows[index - 1] ?? null;

    if (wasUnsaved && this.committingRows.has(row)) return;
    row.remove();
    this.#refreshEmpty();
    this.#focusAfterRemoval(neighbour);
    if (wasUnsaved) {
      this.#paintDraft();
      if (this.helpKey === 'pick_first') this.#setHelp(null);
      return;
    }
    this.save();
  }

  // Deux clics : le brouillon courant prend ces dates, sinon un brouillon naît (un seul à la fois).
  #createPeriod(start, end) {
    const pending = this.#draftRow();
    const row = pending ?? this.#cloneRow();
    if (!row) return;

    this.#setDates(row, start, end, { connected: Boolean(pending) });
    if (pending) {
      this.#datesChanged(row);
    } else {
      row.setAttribute(UNSAVED_ATTRIBUTE, '');
      this.periodListTarget.insertBefore(row, this.#rowAfter(start));
      this.#validateDraft(row);
    }
    this.#closeOtherEditors(row);
    this.#refreshEmpty();
    this.#paintSummary(row);
    this.#syncDraftActions(row);
    this.#paintDraft();
    this.#openEditor(row, { focus: true });
  }

  #cloneRow() {
    if (!this.hasPeriodTemplateTarget || !this.hasPeriodListTarget) return null;
    const id = crypto.randomUUID();
    const fragment = this.periodTemplateTarget.content.cloneNode(true);
    const row = fragment.firstElementChild;
    if (!row) return null;
    // L'id stable de la période remplace `__ID__` dans TOUS les attributs du clone : noms
    // `organization_setting[non_working_periods][<id>][label|start|end]`, ids, `for`, `aria-*`.
    [row, ...row.querySelectorAll('*')].forEach((node) => {
      node.getAttributeNames().forEach((name) => {
        const value = node.getAttribute(name);
        if (value.includes(ID_PLACEHOLDER))
          node.setAttribute(name, value.replaceAll(ID_PLACEHOLDER, id));
      });
    });
    return row;
  }

  // Pose Du / Au : le champ ISO caché (la valeur postée) ET l'affichage JJ/MM/AAAA de `Form::DateInputComponent`.
  // Ligne neuve (pas encore connectée) : le sélecteur lit le champ caché à son `connect()`. Ligne déjà dans la
  // page : un `change` sur le champ caché resynchronise son calendrier (`date_picker_controller.js#onHiddenChange`).
  #setDates(row, start, end, { connected }) {
    this.writing = true;
    try {
      [
        ['start', start],
        ['end', end],
      ].forEach(([bound, iso]) => {
        const hidden = this.#dateField(row, bound);
        if (!hidden) return;
        hidden.value = iso;
        const display = hidden.closest(DATE_PICKER_ROOT)?.querySelector(DATE_PICKER_DISPLAY);
        if (display) display.value = isoToDisplay(iso);
        if (connected) hidden.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } finally {
      this.writing = false;
    }
  }

  #dateField(row, bound) {
    return row.querySelector(`input[name$="[${bound}]"]`);
  }

  #labelField(row) {
    return row.querySelector('input[name$="[label]"]');
  }

  #isComplete(row) {
    return ['start', 'end'].every((bound) => isoParts(this.#dateField(row, bound)?.value));
  }

  // Le brouillon modifiable : celui qui n'est pas en train de partir.
  #draftRow() {
    return (
      this.#rows().find(
        (row) => row.hasAttribute(UNSAVED_ATTRIBUTE) && !this.committingRows.has(row)
      ) ?? null
    );
  }

  // A.15 (30/10) : l'erreur d'un brouillon se lit pendant la saisie, comme dans le tiroir, contre toutes les
  // périodes de la liste (celles des autres années comprises : masquées, jamais omises), le brouillon en dernier.
  // Le serveur garde sa vérification ; une erreur qu'il renvoie reste jusqu'au prochain changement de date.
  #validateDraft(row) {
    const period = (node) => ({
      label: this.#labelField(node)?.value ?? '',
      start: this.#dateField(node, 'start')?.value ?? '',
      end: this.#dateField(node, 'end')?.value ?? '',
    });
    const saved = this.#rows().filter((node) => !node.hasAttribute(UNSAVED_ATTRIBUTE));
    const errors = periodErrors(
      [...saved.map(period), { ...period(row), draft: true }],
      this.messagesValue ?? {}
    );
    this.#setErrorText(row, errors.at(-1));
    this.#syncInvalid();
  }

  #hasError(row) {
    return Boolean(this.#errorNode(row)?.textContent.trim());
  }

  #canCommit(row) {
    return this.#isComplete(row) && !this.#hasError(row);
  }

  // Du ou Au d'un brouillon a changé : son erreur se recalcule sur les nouvelles dates (celle du serveur portait
  // sur les anciennes).
  #datesChanged(row) {
    this.#validateDraft(row);
    if (this.helpKey === 'pick_first' && this.#isComplete(row)) this.#setHelp(null);
  }

  // « Ajouter la période » désactivé (avec sa raison) tant qu'une date manque ou qu'une erreur est affichée,
  // pendant son propre envoi, et après un 409 (plus rien ne part). Réserve DS 2 : le bouton désactivé est décrit
  // par l'erreur, par l'aide `invalid_dates` (le nœud de son `aria-describedby` rendu par le serveur) ou par
  // le message de conflit (`#agency_calendar_alert`).
  #syncDraftActions(row) {
    const commit = row.querySelector(COMMIT_SELECTOR);
    const busy = this.committingRows.has(row);
    const cancel = row.querySelector(CANCEL_DRAFT_SELECTOR);
    if (cancel) cancel.disabled = busy;
    if (!commit) return;

    const missing = !this.#isComplete(row);
    const errored = this.#hasError(row);
    commit.disabled = missing || errored || busy || this.conflicted;

    if (!commit.hasAttribute(HINT_ATTRIBUTE)) {
      commit.setAttribute(HINT_ATTRIBUTE, commit.getAttribute('aria-describedby') || '');
    }
    const hintId = commit.getAttribute(HINT_ATTRIBUTE);
    const hint = hintId ? row.querySelector(`[id="${hintId}"]`) : null;
    if (hint) {
      if (!hint.textContent.trim()) hint.textContent = this.messagesValue?.invalid_dates ?? '';
      hint.hidden = !missing;
    }

    const alert = this.conflicted ? document.getElementById?.(ALERT_ID) : null;
    const reason = errored ? this.#errorNode(row)?.id : missing ? hintId : alert ? ALERT_ID : '';
    if (reason) commit.setAttribute('aria-describedby', reason);
    else commit.removeAttribute('aria-describedby');
  }

  // 200 sur l'envoi du brouillon : la ligne devient une période normale (enregistrement automatique).
  // Un focus resté dans la ligne (Libellé, la ligne elle-même) passe au crayon, visible dès que le marqueur part :
  // l'éditeur qui se ferme le laisserait tomber sur `<body>` (QA-JNT-72).
  #finalizeDraft(row) {
    const active = document.activeElement;
    const focusInRow = !active || active === document.body || row.contains(active);
    row.removeAttribute(UNSAVED_ATTRIBUTE);
    row.querySelectorAll(DRAFT_ACTIONS_SELECTOR).forEach((node) => node.remove());
    this.#closeEditor(row);
    this.#placeInOrder(row);
    if (focusInRow) this.#editButtonOf(row)?.focus();
    this.#paintDraft();
    if (this.helpKey === 'pick_first') this.#setHelp(null);
  }

  // QA-JNT-04 (Nicolas, 30/10) : la période née en tête par « Ajouter » va à sa place chronologique dès son
  // enregistrement, comme celle tracée dans la grille (`#rowAfter`). AVANT le focus : déplacer un nœud qui porte le
  // focus le lui fait perdre.
  #placeInOrder(row) {
    const start = this.#dateField(row, 'start')?.value;
    if (!isoParts(start)) return;
    const next = this.#rowAfter(start);
    if (next !== row) this.periodListTarget.insertBefore(row, next);
  }

  #rows() {
    if (!this.hasPeriodListTarget) return [];
    return Array.from(this.periodListTarget.children).filter((node) =>
      /^period_.+/.test(node.id || '')
    );
  }

  #rowOf(node) {
    if (!node || !this.hasPeriodListTarget) return null;
    const list = this.periodListTarget;
    let current = node;
    while (current && current.parentElement !== list) current = current.parentElement;
    return current && /^period_.+/.test(current.id || '') ? current : null;
  }

  // Ligne devant laquelle insérer une période qui commence le `start` : l'ordre de la liste est chronologique.
  #rowAfter(start) {
    return (
      this.#rows().find((row) => {
        const other = this.#dateField(row, 'start')?.value;
        return isoParts(other) && other > start;
      }) ?? null
    );
  }

  #editorOf(row) {
    const button = this.#editButtonOf(row);
    const id = button?.getAttribute('aria-controls');
    return id ? row.querySelector(`[id="${id}"]`) : null;
  }

  #editButtonOf(row) {
    return row.querySelector('[aria-controls][data-action~="agency-calendar#edit"]');
  }

  #openEditor(row, { focus = false } = {}) {
    const editor = this.#editorOf(row);
    if (editor) editor.hidden = false;
    this.#editButtonOf(row)?.setAttribute('aria-expanded', 'true');
    if (focus) this.#labelField(row)?.focus();
  }

  #closeEditor(row) {
    const editor = this.#editorOf(row);
    if (editor) editor.hidden = true;
    this.#editButtonOf(row)?.setAttribute('aria-expanded', 'false');
  }

  #focusAfterRemoval(neighbour) {
    // Sans voisine : « Ajouter ».
    const target =
      (neighbour && this.#editButtonOf(neighbour)) ||
      this.element.querySelector('[data-action~="agency-calendar#add"]');
    target?.focus();
  }

  #refreshEmpty() {
    this.#showYearPeriods();
    if (this.hasEmptyTarget) this.emptyTarget.hidden = this.#rows().some((row) => !row.hidden);
  }

  #errorNode(row) {
    return row.querySelector(`[id="${row.id}_errors"]`);
  }

  #setErrorText(row, text) {
    const node = this.#errorNode(row);
    if (node) node.textContent = text;
  }

  // Du / Au en erreur tant que le nœud d'erreurs de la ligne (rempli par la réponse du serveur) n'est pas vide.
  #syncInvalid() {
    this.#rows().forEach((row) => {
      const invalid = this.#hasError(row);
      ['start', 'end'].forEach((bound) => {
        const display = this.#dateField(row, bound)
          ?.closest(DATE_PICKER_ROOT)
          ?.querySelector(DATE_PICKER_DISPLAY);
        if (!display) return;
        if (invalid) display.setAttribute('aria-invalid', 'true');
        else display.removeAttribute('aria-invalid');
      });
      if (invalid) this.#openEditor(row);
      if (row.hasAttribute(UNSAVED_ATTRIBUTE)) this.#syncDraftActions(row);
    });
  }

  // ── Enregistrement automatique ────────────────────────────────────────────────────────────────────────────

  handleChange(event) {
    if (this.writing) return;
    const field = event.target;
    if (!this.#isFormField(field)) return;

    const row = this.#rowOf(field);
    if (row && /\[(start|end)\]$/.test(field.name)) {
      anchorEndOnStart(this.#dateField(row, 'end'), this.#dateField(row, 'start')?.value);
    }
    if (row?.hasAttribute(UNSAVED_ATTRIBUTE)) {
      this.#paintSummary(row);
      // Un brouillon ne s'enregistre jamais seul. Pendant son envoi, un changement repartira après.
      if (this.committingRows.has(row)) {
        this.save();
        return;
      }
      if (/\[(start|end)\]$/.test(field.name)) {
        this.#datesChanged(row);
        this.#paintDraft();
      }
      return;
    }
    this.save();
  }

  handleInput(event) {
    if (this.writing) return;
    const field = event.target;
    if (!this.#isFormField(field) || !/\[label\]$/.test(field.name)) return;
    const row = this.#rowOf(field);
    if (row?.hasAttribute(UNSAVED_ATTRIBUTE)) {
      this.#paintSummary(row);
      if (!this.committingRows.has(row)) return;
    }

    clearTimeout(this.labelTimer);
    this.labelTimer = setTimeout(() => this.save(), LABEL_DEBOUNCE_MS);
  }

  #isFormField(field) {
    return Boolean(field && field.name && this.hasFormTarget && this.formTarget.contains(field));
  }

  save() {
    clearTimeout(this.labelTimer);
    if (!this.hasFormTarget) return;
    // Après un 409, toute requête repartirait sur la même empreinte refusée : le message de conflit reste,
    // la saisie reste à l'écran, plus rien ne part (QA-JNT-69).
    if (this.conflicted) {
      this.#releaseCommits(this.committingRows);
      return;
    }
    // En vol, ou réussie sans que sa nouvelle empreinte soit encore dans la page : on renverra plus tard.
    if (this.inFlight || this.awaitingFingerprint) {
      this.dirty = true;
      return;
    }

    this.dirty = false;
    this.#withoutUnsavedRows(() => {
      const state = this.#formState();
      if (state === this.lastSavedState) return;

      this.inFlight = true;
      this.sentState = state;
      this.sentCommitRows = new Set(this.committingRows);
      this.submitted = false;
      this.fingerprintRefreshed = false;
      this.#showStatus('saving');
      // Turbo construit le corps de la requête pendant l'événement `submit`, donc pendant cet appel.
      this.formTarget.requestSubmit();
      if (!this.submitted) {
        // Aucune soumission (contrainte de validation du navigateur) : rien n'est en vol.
        this.inFlight = false;
        this.#showStatus(null);
        this.#releaseCommits(this.sentCommitRows);
        this.sentCommitRows = new Set();
      }
    });
  }

  handleSubmit(event) {
    if (this.hasFormTarget && event.target === this.formTarget) this.submitted = true;
  }

  // Ordre voulu (QA-JNT-69) : TOUS les drapeaux d'abord, les effets sur le DOM ensuite. `#finalizeDraft` rend
  // le focus au crayon, et le navigateur émet alors `change` sur le Libellé quitté : ce `change` appelle
  // `save()`, qui doit déjà savoir qu'on attend la nouvelle empreinte, sinon il part sur l'ancienne (409).
  handleSubmitEnd(event) {
    if (!this.hasFormTarget || event.target !== this.formTarget) return;
    const committed = [...this.sentCommitRows];
    this.sentCommitRows = new Set();

    if (event.detail?.success) {
      this.lastSavedState = this.sentState;
      committed.forEach((row) => this.committingRows.delete(row));
      // La réponse remplace l'empreinte APRÈS cet événement : rien ne repart avant (sinon 409).
      const waitForFingerprint = !this.fingerprintRefreshed;
      if (waitForFingerprint) {
        this.awaitingFingerprint = true;
        clearTimeout(this.fingerprintTimer);
        this.fingerprintTimer = setTimeout(() => this.#fingerprintDone(), FINGERPRINT_WAIT_MS);
      }
      this.inFlight = false;
      committed.forEach((row) => this.#finalizeDraft(row));
      this.#showStatus('saved');
      this.#announce(this.messagesValue?.saved ?? '');
      if (!waitForFingerprint && this.dirty) this.save();
      return;
    }

    // Rien d'enregistré (422, 409, réseau) : le prochain état repart, même identique.
    this.lastSavedState = null;
    // 409 : l'empreinte de la page est périmée, aucun renvoi ne peut réussir. Le message vient du flux.
    if (event.detail?.fetchResponse?.statusCode === 409) this.conflicted = true;
    this.inFlight = false;
    this.#showStatus(null);
    // Le brouillon refusé reste brouillon ; l'erreur arrive par le flux qui suit.
    this.#releaseCommits(this.conflicted ? [...this.committingRows] : committed);
    this.#syncInvalid();
    if (this.dirty && !this.conflicted) this.save();
  }

  #releaseCommits(rows) {
    [...rows].forEach((row) => {
      this.committingRows.delete(row);
      this.#syncDraftActions(row);
    });
  }

  #fingerprintDone() {
    this.fingerprintRefreshed = true;
    if (!this.awaitingFingerprint) return;
    this.awaitingFingerprint = false;
    clearTimeout(this.fingerprintTimer);
    if (this.dirty) this.save();
  }

  // Les champs d'un brouillon (sauf celui qu'« Ajouter la période » envoie) perdent leur nom le temps de
  // construire l'état et la requête : Turbo lit le formulaire pendant `requestSubmit()`.
  #withoutUnsavedRows(callback) {
    const stashed = [];
    this.#rows()
      .filter((row) => row.hasAttribute(UNSAVED_ATTRIBUTE) && !this.committingRows.has(row))
      .forEach((row) => {
        row.querySelectorAll('[name]').forEach((field) => {
          field.setAttribute(STASHED_NAME_ATTRIBUTE, field.getAttribute('name'));
          field.removeAttribute('name');
          stashed.push(field);
        });
      });
    try {
      return callback();
    } finally {
      stashed.forEach((field) => {
        field.setAttribute('name', field.getAttribute(STASHED_NAME_ATTRIBUTE));
        field.removeAttribute(STASHED_NAME_ATTRIBUTE);
      });
    }
  }

  // Les paires (nom, valeur) que le formulaire posterait, hors champs techniques.
  #formState() {
    const pairs = [];
    this.formTarget
      .querySelectorAll('input[name], select[name], textarea[name]')
      .forEach((field) => {
        if (field.disabled || VOLATILE_FIELDS.has(field.name)) return;
        if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
        pairs.push([field.name, field.value]);
      });
    return JSON.stringify(pairs);
  }

  #showStatus(state) {
    if (!this.hasStatusTarget) return;
    if (state) this.statusTarget.hidden = false;
    this.statusTarget.querySelectorAll('[data-state]').forEach((node) => {
      node.hidden = node.dataset.state !== state;
    });
  }

  #announce(text) {
    if (!this.hasAnnouncerTarget || !text) return;
    this.announcerTarget.textContent = '';
    clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      if (this.hasAnnouncerTarget) this.announcerTarget.textContent = text;
    }, ANNOUNCE_DELAY_MS);
  }

  // ── Rendus venus du serveur ───────────────────────────────────────────────────────────────────────────────

  handleFrameLoad() {
    this.#repaint({ refocus: false });
    this.#refreshEmpty();
  }

  // La liste ne montre que les périodes qui touchent l'année affichée (retour de Nicolas, 30/10), comme le rendu
  // serveur (`PlanningCalendarView::PeriodRow#touches_year?`). Masquées, jamais retirées : le formulaire poste la
  // liste entière. Un brouillon, une ligne ouverte ou en erreur reste montrée, même hors de l'année.
  #showYearPeriods() {
    const year = this.#displayedYear();
    this.#rows().forEach((row) => {
      const editor = this.#editorOf(row);
      const kept =
        row.hasAttribute(UNSAVED_ATTRIBUTE) || (editor && !editor.hidden) || this.#hasError(row);
      const start = this.#dateField(row, 'start')?.value;
      const end = this.#dateField(row, 'end')?.value;
      row.hidden = !kept && !periodTouchesYear(start, end, year);
    });
  }

  // Enveloppe le rendu de chaque flux Turbo : une fois le DOM remplacé, l'état posé par ce contrôleur revient.
  handleBeforeStreamRender(event) {
    const render = event.detail?.render;
    if (typeof render !== 'function') return;
    const gridHadFocus = Boolean(document.activeElement?.matches?.(DAY_SELECTOR));
    event.detail.render = async (stream) => {
      await render(stream);
      if (this.connected) this.#afterStream(stream, gridHadFocus);
    };
  }

  #afterStream(stream, gridHadFocus) {
    if (stream?.getAttribute?.('target') === FINGERPRINT_ID) this.#fingerprintDone();
    this.#repaint({ refocus: gridHadFocus });
    this.#syncExpanded();
    this.#syncInvalid();
    this.#rows().forEach((row) => this.#syncEditName(row));
  }

  // Après un frame (année) ou une grille rendue par le serveur : la plage en cours, le férié mis en avant,
  // l'arrêt de tabulation (et le focus, s'il était sur un jour remplacé) et l'aide reviennent.
  #repaint({ refocus }) {
    this.#paintDraft();
    this.#rows()
      .filter((row) => row.hasAttribute(UNSAVED_ATTRIBUTE))
      .forEach((row) => this.#paintSummary(row));
    this.#paintHighlight();
    this.#paintHelp();
    this.#syncYearField();

    const cell = this.#cellFor(this.focusDate);
    if (!cell) return;
    const focusLost = !document.activeElement || document.activeElement === document.body;
    this.#rove(cell, { focus: refocus && focusLost });
  }

  // La réponse re-rend le résumé d'une ligne, crayon compris : son `aria-expanded` suit l'éditeur.
  #syncExpanded() {
    this.#rows().forEach((row) => {
      const editor = this.#editorOf(row);
      if (editor) {
        this.#editButtonOf(row)?.setAttribute('aria-expanded', editor.hidden ? 'false' : 'true');
      }
    });
  }

  // Le résumé d'un brouillon (A·e) : libellé, dates et « N j » composés comme le serveur, par `textContent`.
  #paintSummary(row) {
    const start = this.#dateField(row, 'start')?.value;
    const end = this.#dateField(row, 'end')?.value;
    // Un brouillon sans libellé s'intitule « Nouvelle période », comme dans le tiroir (DS-2, 30/10) ; enregistré, le
    // serveur lui rend « Période non travaillée ». Ce résumé n'est peint que sur les brouillons.
    const messages = { ...this.messagesValue, default_label: this.messagesValue?.new_label };
    const summary = periodSummary(
      { start, end, label: this.#labelField(row)?.value },
      { messages, calendar: this.calendarValue, year: null }
    );
    // Dates refusées par le concern (années, 366 jours) : aucun compte, le message d'erreur suffit (QA-JNT-59).
    const days = periodDateErrorKey(start, end) === null ? summary.days : '';
    [
      [PERIOD_LABEL_SELECTOR, summary.label],
      ['[data-period-dates]', summary.dates],
      ['[data-period-days]', days],
    ].forEach(([selector, text]) => {
      const node = row.querySelector(selector);
      if (node) node.textContent = text;
    });
    this.#syncEditName(row);
  }

  // Nom du crayon = gabarit du serveur + libellé AFFICHÉ de la ligne (QA-JNT-04-crayon), par attributs.
  #syncEditName(row) {
    const button = this.#editButtonOf(row);
    const template = button?.getAttribute(EDIT_LABEL_ATTRIBUTE);
    const label = row.querySelector(PERIOD_LABEL_SELECTOR)?.textContent.trim();
    if (!template || !label) return;
    const name = interpolate(template, { label });
    if (button.getAttribute('aria-label') !== name) button.setAttribute('aria-label', name);
    if (button.getAttribute('title') !== name) button.setAttribute('title', name);
  }

  #displayedYear() {
    const first = this.dayTargets[0]?.dataset.date;
    return isoParts(first)?.[0] ?? (this.hasYearValue ? this.yearValue : null);
  }

  // L'année postée suit l'année affichée (le serveur re-rend la grille de `year`).
  #syncYearField() {
    if (!this.hasFormTarget) return;
    const field = this.formTarget.querySelector('input[name="year"]');
    const year = this.#displayedYear();
    if (field && year) field.value = String(year);
  }
}
